import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  KeyRound,
  Pause,
  Play,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { Modal } from '../../../shared/components/ui/Modal.js';
import { useAuthStore } from '../../../stores/auth-store.js';
import { superUsersApi, type SuperUserRow, type SyncStatus } from '../api.js';

export function SuperUsersPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [resetUser, setResetUser] = useState<SuperUserRow | null>(null);
  const [syncDetailUser, setSyncDetailUser] = useState<SuperUserRow | null>(null);
  const currentUser = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['super-users'],
    queryFn: () => superUsersApi.list(),
  });

  const users = usersQuery.data ?? [];

  // Sync status batch — uma query por user. Carrega só pra users com TOTP setup.
  const syncQueries = useQueries({
    queries: users.map((u) => ({
      queryKey: ['super-user-sync', u.id],
      queryFn: () => superUsersApi.syncStatus(u.id),
      enabled: u.totp_confirmed,
      staleTime: 30_000,
    })),
  });

  const handleDelete = async (u: SuperUserRow): Promise<void> => {
    if (u.id === currentUser?.id) {
      toast.error('Você não pode deletar a si mesmo');
      return;
    }
    if (!window.confirm(`Remover super-admin "${u.email}"?\n\nIsso também remove dos servidores ChatPlus cadastrados.`)) return;
    try {
      await superUsersApi.delete(u.id);
      toast.success('Super-admin removido');
      void qc.invalidateQueries({ queryKey: ['super-users'] });
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    }
  };

  const toggleMut = useMutation({
    mutationFn: async ({ u, newStatus }: { u: SuperUserRow; newStatus: 'active' | 'inactive' }) =>
      superUsersApi.patch(u.id, { status: newStatus }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      void qc.invalidateQueries({ queryKey: ['super-user-sync', vars.u.id] });
      toast.success(
        vars.newStatus === 'active'
          ? `"${vars.u.email}" reativado`
          : `"${vars.u.email}" desativado`,
      );
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const resyncMut = useMutation({
    mutationFn: async (id: number) => superUsersApi.resync(id),
    onSuccess: (data, id) => {
      void qc.invalidateQueries({ queryKey: ['super-user-sync', id] });
      toast.success('Re-sync disparado', {
        description: `${data.enqueued} servidor(es) → ${data.succeeded} OK, ${data.failed} falhou`,
      });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-n-900">Super Users</h1>
          <p className="text-[13px] text-n-500">
            Administradores do super-painel. Email deve terminar em <code className="font-mono text-brand-600">@chatplusbr.com</code>.
            Replicados automaticamente nos servidores ChatPlus cadastrados.
          </p>
        </div>
        <Button onClick={() => setModalOpen(true)}>
          <Plus size={14} /> Adicionar Super-Admin
        </Button>
      </div>

      <Card>
        <table className="w-full">
          <thead className="border-b border-n-200 bg-n-50 text-left text-[11px] font-semibold uppercase text-n-500">
            <tr>
              <th className="px-5 py-3">Nome / Email</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">TOTP</th>
              <th className="px-5 py-3">Sincronizado</th>
              <th className="px-5 py-3">Último login</th>
              <th className="px-5 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-[13px] text-n-500">
                  Carregando...
                </td>
              </tr>
            )}
            {users.map((u, idx) => {
              const syncData = syncQueries[idx]?.data;
              const isMe = u.id === currentUser?.id;
              return (
                <tr key={u.id} className="border-b border-n-100 hover:bg-n-50">
                  <td className="px-5 py-3">
                    <div className="text-[13px] font-medium text-n-900">
                      {u.name}
                      {isMe && (
                        <span className="ml-2 rounded-pill bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700">
                          VOCÊ
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-n-500 font-mono">{u.email}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={
                        u.status === 'active'
                          ? 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700'
                          : 'rounded-pill bg-n-100 px-2 py-0.5 text-[10px] font-semibold text-n-600'
                      }
                    >
                      {u.status === 'active' ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {u.totp_confirmed ? (
                      <span className="inline-flex items-center gap-1 text-[12px] text-success-700">
                        <ShieldCheck size={12} /> Configurado
                      </span>
                    ) : (
                      <span className="text-[12px] text-warning-600">Pendente</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <SyncBadge
                      data={syncData}
                      loading={syncQueries[idx]?.isLoading}
                      totpReady={u.totp_confirmed}
                      onClick={() => setSyncDetailUser(u)}
                    />
                  </td>
                  <td className="px-5 py-3 text-[12px] text-n-500">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setResetUser(u)}
                        title="Resetar senha"
                      >
                        <KeyRound size={12} />
                        Senha
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resyncMut.mutate(u.id)}
                        loading={resyncMut.isPending && resyncMut.variables === u.id}
                        title="Forçar re-sincronização"
                        disabled={!u.totp_confirmed}
                      >
                        <RefreshCw size={12} />
                      </Button>
                      {!isMe && (
                        <>
                          <Button
                            variant={u.status === 'active' ? 'outline' : 'primary'}
                            size="sm"
                            onClick={() =>
                              toggleMut.mutate({
                                u,
                                newStatus: u.status === 'active' ? 'inactive' : 'active',
                              })
                            }
                            loading={toggleMut.isPending && toggleMut.variables?.u.id === u.id}
                          >
                            {u.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                          </Button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(u)}
                            className="rounded-md p-1.5 text-n-500 hover:bg-danger-50 hover:text-danger-600"
                            title="Remover"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <CreateSuperUserModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />
      <SyncDetailModal user={syncDetailUser} onClose={() => setSyncDetailUser(null)} />
    </div>
  );
}

/* ─── Badge de status de sync ──────────────────────────────────── */

function SyncBadge({
  data,
  loading,
  totpReady,
  onClick,
}: {
  data: SyncStatus | undefined;
  loading: boolean | undefined;
  totpReady: boolean;
  onClick: () => void;
}) {
  if (!totpReady) {
    return <span className="text-[11px] text-n-400">—</span>;
  }
  if (loading || !data) {
    return <span className="text-[11px] text-n-400">...</span>;
  }
  if (data.total === 0) {
    return <span className="text-[11px] text-n-400">Sem servidores</span>;
  }
  const allOk = data.success === data.total && data.failed === 0;
  const hasFailure = data.failed > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5 text-[11px] font-semibold transition ${
        allOk
          ? 'bg-success-50 text-success-700 hover:bg-success-100'
          : hasFailure
            ? 'bg-danger-50 text-danger-700 hover:bg-danger-100'
            : 'bg-warning-50 text-warning-700 hover:bg-warning-100'
      }`}
      title="Ver detalhes de sincronização"
    >
      {allOk ? (
        <CheckCircle2 size={12} />
      ) : hasFailure ? (
        <AlertTriangle size={12} />
      ) : (
        <Clock size={12} />
      )}
      {data.success}/{data.total}
    </button>
  );
}

/* ─── Modal de detalhe de sync ─────────────────────────────────── */

function SyncDetailModal({ user, onClose }: { user: SuperUserRow | null; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['super-user-sync', user?.id],
    queryFn: () => (user ? superUsersApi.syncStatus(user.id) : Promise.resolve(null)),
    enabled: !!user,
  });

  if (!user) return null;

  return (
    <Modal open={!!user} onClose={onClose} title={`Sincronização — ${user.name}`} width={620}>
      {detailQuery.isLoading && (
        <div className="py-6 text-center text-[13px] text-n-500">Carregando...</div>
      )}
      {detailQuery.data && (
        <>
          <div className="mb-4 grid grid-cols-3 gap-3">
            <StatusTile label="Sincronizados" count={detailQuery.data.success} variant="success" />
            <StatusTile label="Pendentes" count={detailQuery.data.pending} variant="warning" />
            <StatusTile label="Falharam" count={detailQuery.data.failed} variant="danger" />
          </div>
          <div className="overflow-hidden rounded-md border border-n-200">
            <table className="w-full">
              <thead className="border-b border-n-200 bg-n-50 text-left text-[11px] font-semibold uppercase text-n-500">
                <tr>
                  <th className="px-3 py-2">Servidor</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Tentativas</th>
                  <th className="px-3 py-2">Último try</th>
                </tr>
              </thead>
              <tbody>
                {detailQuery.data.details.map((d) => (
                  <tr key={d.server_id} className="border-b border-n-100 last:border-b-0 align-top">
                    <td className="px-3 py-2 text-[12px] font-medium text-n-900">
                      {d.server_name}
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill status={d.status} />
                      {d.last_error && (
                        <div className="mt-1 max-w-[260px] text-[10px] text-danger-600 font-mono whitespace-pre-wrap break-all">
                          {d.last_error.slice(0, 200)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-n-600">{d.attempts}</td>
                    <td className="px-3 py-2 text-[11px] text-n-500">
                      {d.last_attempt_at
                        ? new Date(d.last_attempt_at).toLocaleString('pt-BR')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}

function StatusTile({
  label,
  count,
  variant,
}: {
  label: string;
  count: number;
  variant: 'success' | 'warning' | 'danger';
}) {
  const colorByVariant: Record<typeof variant, string> = {
    success: 'border-success-500/30 bg-success-50 text-success-700',
    warning: 'border-warning-500/30 bg-warning-50 text-warning-700',
    danger: 'border-danger-500/30 bg-danger-50 text-danger-700',
  };
  return (
    <div className={`rounded-md border p-3 ${colorByVariant[variant]}`}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-80">{label}</div>
      <div className="mt-1 text-[24px] font-bold">{count}</div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const labels: Record<string, string> = {
    success: 'OK',
    pending: 'Pendente',
    in_progress: 'Processando',
    failed: 'Falhou',
    never_synced: 'Nunca',
  };
  const classes: Record<string, string> = {
    success: 'bg-success-50 text-success-700',
    pending: 'bg-warning-50 text-warning-700',
    in_progress: 'bg-brand-50 text-brand-700',
    failed: 'bg-danger-50 text-danger-700',
    never_synced: 'bg-n-100 text-n-600',
  };
  return (
    <span
      className={`rounded-pill px-2 py-0.5 text-[10px] font-semibold uppercase ${classes[status] ?? 'bg-n-100 text-n-600'}`}
    >
      {labels[status] ?? status}
    </span>
  );
}

/* ─── Modal criar super-admin ───────────────────────────────────── */

function CreateSuperUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const qc = useQueryClient();

  const reset = (): void => {
    setName('');
    setEmail('');
    setPassword('');
  };

  const mut = useMutation({
    mutationFn: async () => superUsersApi.create({ name, email, password }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super-users'] });
      toast.success('Super-admin criado', {
        description: 'No primeiro login será forçado o setup TOTP. Depois a credencial será replicada nos servidores.',
      });
      reset();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="Adicionar Super-Admin" width={480}>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <Input
          label="Nome"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="nome.master@chatplusbr.com"
          hint="Convenção: deve terminar em @chatplusbr.com pra não conflitar com admins de tenants"
        />
        <Input
          label="Senha"
          type="text"
          required
          minLength={8}
          maxLength={120}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="No mínimo 8 caracteres. Passe ao novo admin pelo canal seguro."
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mut.isPending}>
            Adicionar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ─── Modal resetar senha ────────────────────────────────────────── */

function ResetPasswordModal({
  user,
  onClose,
}: {
  user: SuperUserRow | null;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: async () => {
      if (!user) return;
      await superUsersApi.resetPassword(user.id, password);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super-user-sync', user?.id] });
      toast.success(`Senha de "${user?.email}" resetada`, {
        description: 'Nova senha já replicada nos servidores. Repasse pelo canal seguro.',
      });
      setPassword('');
      setConfirm('');
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const passwordsMatch = password.length >= 8 && password === confirm;

  return (
    <Modal
      open={!!user}
      onClose={() => {
        setPassword('');
        setConfirm('');
        onClose();
      }}
      title={`Resetar senha — ${user?.name ?? ''}`}
      width={420}
    >
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (passwordsMatch) mut.mutate();
        }}
        className="flex flex-col gap-4"
      >
        <div className="rounded-md bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
          A nova senha é replicada automaticamente em todos os servidores ChatPlus cadastrados.
        </div>
        <Input
          label="Nova senha"
          type="text"
          required
          minLength={8}
          maxLength={120}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
        />
        <Input
          label="Confirmar nova senha"
          type="text"
          required
          minLength={8}
          maxLength={120}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          {...(confirm.length > 0 && confirm !== password
            ? { error: 'Senhas não conferem' }
            : {})}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="danger" loading={mut.isPending} disabled={!passwordsMatch}>
            Resetar senha
          </Button>
        </div>
      </form>
    </Modal>
  );
}
